import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FolderKanban, CalendarClock, BellRing, AlertTriangle, Pickaxe, Search as SearchIcon,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, Disclaimer } from "@/components/common";
import { Sparkles, ChevronRight } from "lucide-react";

const CARD_DEFS = [
    { key: "processos", label: "Processos Cadastrados", icon: FolderKanban, color: "text-petroleum" },
    { key: "prazos_proximos", label: "Prazos Próximos", icon: CalendarClock, color: "text-blue-600" },
    { key: "alertas", label: "Alertas", icon: BellRing, color: "text-mineral" },
    { key: "pendencias", label: "Pendências", icon: AlertTriangle, color: "text-amber-600" },
    { key: "em_pesquisa", label: "Processos em Pesquisa", icon: SearchIcon, color: "text-teal-600" },
    { key: "em_lavra", label: "Processos em Lavra", icon: Pickaxe, color: "text-indigo-600" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    }, []);

    const cards = data?.cards || {};
    const upcoming = data?.proximos_vencimentos || [];

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div>
                <h1 className="font-heading text-2xl md:text-3xl font-black text-slate-900 tracking-tight" data-testid="dashboard-greeting">
                    Olá, {user?.name?.split(" ")[0] || "profissional"}
                </h1>
                <p className="text-slate-500 mt-1 text-sm">Resumo da sua carteira mineral.</p>
            </div>

            {data?.prazos_auto_pendentes > 0 && (
                <button onClick={() => navigate("/prazos?tab=pendentes")} data-testid="dashboard-auto-pending-banner"
                    className="w-full text-left bg-mineral/10 border border-mineral/30 rounded-md p-4 flex items-center justify-between gap-4 hover:bg-mineral/15 transition-colors">
                    <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-mineralHover shrink-0 mt-0.5" />
                        <div>
                            <div className="font-heading font-bold text-slate-800">{data.prazos_auto_pendentes} prazo(s) detectado(s) pelo GM² aguardando sua confirmação</div>
                            <p className="text-sm text-slate-600 mt-0.5">O GM² identificou possíveis obrigações a partir do DOU / eventos ANM. Revise e confirme antes de usar como prazo definitivo.</p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-mineralHover shrink-0" />
                </button>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {CARD_DEFS.map((c) => (
                    <div key={c.key} className="bg-white border border-slate-200 rounded-md p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all" data-testid={`kpi-${c.key}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{c.label}</span>
                            <c.icon className={`w-5 h-5 ${c.color}`} />
                        </div>
                        <div className="mt-3 text-3xl font-black text-slate-900 font-mono">{cards[c.key] ?? "—"}</div>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-md shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <h2 className="font-heading font-bold text-slate-800">Próximos Vencimentos</h2>
                    <button onClick={() => navigate("/prazos")} className="text-sm text-petroleum hover:text-petroleumHover font-medium" data-testid="dashboard-see-all-deadlines">Ver todos</button>
                </div>
                {upcoming.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">Nenhum vencimento próximo cadastrado.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b-2 border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                                    <th className="py-3 px-4">Processo ANM</th>
                                    <th className="py-3 px-4">Obrigação</th>
                                    <th className="py-3 px-4">Data limite</th>
                                    <th className="py-3 px-4">Dias restantes</th>
                                    <th className="py-3 px-4">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcoming.map((d) => (
                                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`deadline-row-${d.id}`}>
                                        <td className="py-3 px-4 font-mono text-petroleum font-semibold">{d.process_number || "—"}</td>
                                        <td className="py-3 px-4 text-slate-700">{d.obligation_type}</td>
                                        <td className="py-3 px-4 font-mono text-slate-600">{d.due_date ? new Date(d.due_date).toLocaleDateString("pt-BR") : "—"}</td>
                                        <td className="py-3 px-4 font-mono font-semibold">{d.days_left != null ? `${d.days_left} dias` : "—"}</td>
                                        <td className="py-3 px-4"><StatusBadge status={d.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Disclaimer />
        </div>
    );
}
